import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import { CopyToClipboard } from "@amzn/awsui-components-react/polaris";

import { RootState } from "../../app/rootReducer";
import {
  FullAsinWithAudits,
  RestrictedAsin,
} from "../../../from-server/asin-central/model";

export interface CopyAllAsinsButtonProps {
  readonly selectedAsins?: readonly FullAsinWithAudits[];
}

const COPY_BUTTON_TEXT = "Copy";
const DISABLED_REASON_ZERO = "No ASINs to copy";
const COPY_ERROR_TEXT = "Failed to copy ASINs";

function hasAsin(
  row: FullAsinWithAudits | RestrictedAsin
): row is FullAsinWithAudits {
  const asin = (row as { asin?: unknown }).asin;
  return typeof asin === "string" && asin.length > 0;
}

export const CopyAllAsinsButton: React.FC<CopyAllAsinsButtonProps> = ({
  selectedAsins,
}) => {
  const currentPagePorAsins = useSelector(
    (state: RootState) => state.porAsinList.currentPagePorAsins
  );

  const hasSelection = (selectedAsins?.length ?? 0) > 0;

  const asinsToCopy = useMemo(() => {
    const source: readonly (FullAsinWithAudits | RestrictedAsin)[] =
      hasSelection
        ? (selectedAsins as readonly FullAsinWithAudits[])
        : currentPagePorAsins;
    return source.filter(hasAsin).map((row) => row.asin);
  }, [hasSelection, selectedAsins, currentPagePorAsins]);

  const textToCopy = asinsToCopy.join("\n");
  const disabled = asinsToCopy.length === 0;
  const ariaLabel = hasSelection
    ? `Copy ${selectedAsins?.length ?? 0} selected ASINs`
    : "Copy ASINs on this page";
  const copySuccessText = `Copied ${asinsToCopy.length} ASIN${
    asinsToCopy.length === 1 ? "" : "s"
  } to clipboard`;

  return (
    <CopyToClipboard
      data-testid="copy-all-asins-button"
      variant="button"
      copyButtonText={COPY_BUTTON_TEXT}
      copyButtonAriaLabel={ariaLabel}
      textToCopy={textToCopy}
      copySuccessText={copySuccessText}
      copyErrorText={COPY_ERROR_TEXT}
      disabled={disabled}
      disabledReason={disabled ? DISABLED_REASON_ZERO : undefined}
    />
  );
};

export default CopyAllAsinsButton;
